import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';

import { AdminService } from 'src/app/_services/admin.service';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminComponent implements OnInit {
  users: any[] = [];
  roles: any[] = [];
  rolesByUser: Record<number, string[]> = {};
  loadingUsers = false;

  activity: any[] = [];
  activityTotal = 0;
  activityRows = 25;
  loadingActivity = false;

  schedule: any = null;
  savingSchedule = false;
  frequencyOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
  ];
  weekdayOptions = [
    { label: 'Monday', value: 0 },
    { label: 'Tuesday', value: 1 },
    { label: 'Wednesday', value: 2 },
    { label: 'Thursday', value: 3 },
    { label: 'Friday', value: 4 },
    { label: 'Saturday', value: 5 },
    { label: 'Sunday', value: 6 },
  ];
  hourOptions = Array.from({ length: 24 }, (_, hour) => ({
    label: `${String(hour).padStart(2, '0')}:00`,
    value: hour,
  }));

  createDialogVisible = false;
  newUserEmail = '';
  newUserFullName = '';
  newUserRoleId: number | null = null;
  creatingUser = false;
  savingRoleFor: number | null = null;

  audit: any[] = [];
  auditTotal = 0;
  auditRows = 50;
  loadingAudit = false;

  emails: any[] = [];
  emailsTotal = 0;
  emailRows = 50;
  loadingEmails = false;

  editDialogVisible = false;
  editUser: any = null;
  editEmail = '';
  editFullName = '';
  savingEdit = false;

  passwordDialogVisible = false;
  passwordUser: any = null;
  newPassword = '';
  savingPassword = false;

  sessionsDialogVisible = false;
  sessionsUser: any = null;
  sessions: any[] = [];

  // --- AI / predictions ---------------------------------------------
  providers: any[] = [];
  loadingProviders = false;
  selectedProvider = 'openai';
  providerModel = 'gpt-4.1-mini';
  providerApiKey = '';
  savingProvider = false;
  testingProvider: number | null = null;
  recomputing = false;
  providerOptions = [
    { label: 'OpenAI', value: 'openai' },
    { label: 'Anthropic (Claude)', value: 'anthropic' },
    { label: 'Google (Gemini)', value: 'google' },
  ];
  // Model choices per provider; the first is the sensible low-cost default.
  modelsByProvider: Record<string, { label: string; value: string }[]> = {
    openai: [
      { label: 'GPT-4.1 mini (fast, low cost)', value: 'gpt-4.1-mini' },
      { label: 'GPT-4.1', value: 'gpt-4.1' },
      { label: 'GPT-4o', value: 'gpt-4o' },
      { label: 'GPT-4o mini', value: 'gpt-4o-mini' },
    ],
    anthropic: [
      { label: 'Claude Sonnet 5', value: 'claude-sonnet-5' },
      { label: 'Claude Haiku 4.5', value: 'claude-haiku-4-5' },
      { label: 'Claude Opus 4.5', value: 'claude-opus-4-5' },
    ],
    google: [
      { label: 'Gemini 2.5 Flash', value: 'gemini-2.5-flash' },
      { label: 'Gemini 2.5 Pro', value: 'gemini-2.5-pro' },
    ],
  };

  get providerModelOptions() {
    return this.modelsByProvider[this.selectedProvider] ?? [];
  }

  // Switching provider resets the model to that provider's saved model (if a
  // config exists) or its default first option.
  onProviderChange(provider: string): void {
    this.selectedProvider = provider;
    const existing = this.providers.find((p) => p.provider === provider);
    this.providerModel =
      existing?.model_name || this.providerModelOptions[0]?.value || '';
    this.providerApiKey = '';
  }

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // The API 403s non-admins anyway; this just avoids a broken screen.
    this.adminService.isAdmin().subscribe((isAdmin) => {
      if (!isAdmin) {
        this.router.navigateByUrl('/');
        return;
      }
      this.loadUsers();
      this.loadActivity(0);
      this.loadAudit(0);
      this.loadEmails(0);
      this.loadSchedule();
      this.loadProviders();
    });
  }

  // --- AI provider config + predictions -------------------------------
  loadProviders(): void {
    this.loadingProviders = true;
    this.adminService.listProviders().subscribe({
      next: (providers) => {
        this.providers = providers ?? [];
        this.loadingProviders = false;
        // Preselect whichever provider is the active default, so the form
        // reflects what's actually driving recommendations.
        const active =
          this.providers.find((p) => p.is_default && p.is_active) ??
          this.providers.find((p) => p.is_active) ??
          null;
        if (active) {
          this.selectedProvider = active.provider;
          if (active.model_name) this.providerModel = active.model_name;
        }
      },
      error: () => {
        this.providers = [];
        this.loadingProviders = false;
      },
    });
  }

  // The provider config for whatever is currently selected in the form.
  // Saving with a key present replaces it; the key is write-only (never
  // returned by the API — only has_api_key comes back).
  get activeProvider(): any | null {
    return (
      this.providers.find((p) => p.provider === this.selectedProvider) ?? null
    );
  }

  // Predictions use the one config marked default+active. Whichever provider
  // is saved here becomes that default; any other provider's default flag is
  // cleared so exactly one wins.
  private clearOtherDefaults(keepProvider: string): void {
    this.providers
      .filter((p) => p.provider !== keepProvider && p.is_default)
      .forEach((p) =>
        this.adminService.updateProvider(p.id, { is_default: false }).subscribe()
      );
  }

  saveProvider(): void {
    const existing = this.activeProvider;
    this.savingProvider = true;
    const done = (msg: string) => {
      this.savingProvider = false;
      this.providerApiKey = '';
      this.clearOtherDefaults(this.selectedProvider);
      this.toastOk(msg);
      this.loadProviders();
    };
    const fail = () => {
      this.savingProvider = false;
      this.toastError('Could not save AI provider');
    };

    if (existing) {
      const body: any = {
        model_name: this.providerModel,
        is_active: true,
        is_default: true,
      };
      if (this.providerApiKey.trim()) body.api_key = this.providerApiKey.trim();
      this.adminService.updateProvider(existing.id, body).subscribe({
        next: () => done('AI provider updated'),
        error: fail,
      });
    } else {
      this.adminService
        .createProvider({
          provider: this.selectedProvider,
          model_name: this.providerModel,
          api_key: this.providerApiKey.trim() || undefined,
          is_default: true,
        })
        .subscribe({ next: () => done('AI provider saved'), error: fail });
    }
  }

  testProvider(): void {
    const existing = this.activeProvider;
    if (!existing) return;
    this.testingProvider = existing.id;
    this.adminService.testProvider(existing.id).subscribe({
      next: (res) => {
        this.testingProvider = null;
        if (res.ok) this.toastOk(res.message || 'Provider reachable');
        else this.toastError(res.message || 'Provider test failed');
      },
      error: () => {
        this.testingProvider = null;
        this.toastError('Provider test failed');
      },
    });
  }

  recomputePredictions(): void {
    this.recomputing = true;
    this.adminService.recomputePredictions().subscribe({
      next: (res) => {
        this.recomputing = false;
        this.toastOk(res.message || 'Predictions recomputed');
      },
      error: () => {
        this.recomputing = false;
        this.toastError('Could not recompute predictions');
      },
    });
  }

  // --- users ---------------------------------------------------------
  loadUsers(): void {
    this.loadingUsers = true;
    forkJoin({
      users: this.adminService.listUsers(),
      roles: this.adminService.listRoles(),
    }).subscribe({
      next: ({ users, roles }) => {
        this.users = users;
        this.roles = roles;
        this.loadingUsers = false;
        users.forEach((user) =>
          this.adminService.userRoles(user.id).subscribe((response: any) => {
            const names = (response?.roles ?? response ?? []).map(
              (role: any) => role.name ?? role
            );
            this.rolesByUser[user.id] = names;
          })
        );
      },
      error: () => {
        this.loadingUsers = false;
        this.toastError('Could not load users');
      },
    });
  }

  isUserAdmin(user: any): boolean {
    return (this.rolesByUser[user.id] || []).includes('admin');
  }

  // Roles shown in the pickers, ordered least→most privileged.
  get roleOptions() {
    const order = ['viewer', 'operator', 'admin'];
    return [...this.roles]
      .filter((role) => order.includes(role.name))
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
      .map((role) => ({
        label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
        value: role.id,
      }));
  }

  // VTT uses one role per user; the primary role is the highest-privileged
  // one currently assigned.
  primaryRoleId(user: any): number | null {
    const order = ['admin', 'operator', 'viewer'];
    const names = this.rolesByUser[user.id] || [];
    for (const name of order) {
      if (names.includes(name)) {
        const role = this.roles.find((r) => r.name === name);
        if (role) {
          return role.id;
        }
      }
    }
    return null;
  }

  // Assign the chosen role and strip every other known role, so a user ends
  // up with exactly one. Superuser (max) is left alone.
  onRoleChange(user: any, roleId: number): void {
    this.savingRoleFor = user.id;
    this.adminService.assignRole(user.id, roleId).subscribe({
      next: () => {
        const toRemove = this.roles.filter(
          (role) =>
            role.id !== roleId &&
            (this.rolesByUser[user.id] || []).includes(role.name)
        );
        const target = this.roles.find((role) => role.id === roleId);
        const finish = () => {
          this.rolesByUser[user.id] = target ? [target.name] : [];
          this.savingRoleFor = null;
          this.toastOk(`${user.email} is now ${target?.name}`);
        };
        if (toRemove.length === 0) {
          finish();
          return;
        }
        let pending = toRemove.length;
        toRemove.forEach((role) =>
          this.adminService.removeRole(user.id, role.id).subscribe({
            next: () => --pending === 0 && finish(),
            error: () => --pending === 0 && finish(),
          })
        );
      },
      error: () => {
        this.savingRoleFor = null;
        this.toastError('Could not change role');
      },
    });
  }

  toggleActive(user: any): void {
    this.adminService.setUserActive(user.id, !user.is_active).subscribe({
      next: (updated) => {
        user.is_active = updated.is_active;
        this.toastOk(
          `${user.email} ${updated.is_active ? 'activated' : 'deactivated'}`
        );
      },
      error: () => this.toastError('Could not update user'),
    });
  }

  openCreateDialog(): void {
    this.newUserEmail = '';
    this.newUserFullName = '';
    const operator = this.roles.find((role) => role.name === 'operator');
    this.newUserRoleId = operator ? operator.id : this.roleOptions[0]?.value ?? null;
    this.createDialogVisible = true;
  }

  createUser(): void {
    const email = this.newUserEmail.trim().toLowerCase();
    if (!email) {
      return;
    }
    this.creatingUser = true;
    this.adminService
      .createUser(email, this.newUserFullName.trim() || null)
      .subscribe({
        next: (user) => {
          const chosen =
            this.roles.find((role) => role.id === this.newUserRoleId) ||
            this.roles.find((role) => role.name === 'operator');
          if (chosen) {
            this.adminService.assignRole(user.id, chosen.id).subscribe();
            this.rolesByUser[user.id] = [chosen.name];
          }
          this.creatingUser = false;
          this.createDialogVisible = false;
          this.toastOk(`Invite sent to ${email}`);
          this.loadUsers();
        },
        error: (error) => {
          this.creatingUser = false;
          this.toastError(
            error?.error?.error?.message || 'Could not create user'
          );
        },
      });
  }

  openSessions(user: any): void {
    this.sessionsUser = user;
    this.sessions = [];
    this.sessionsDialogVisible = true;
    this.adminService.userSessions(user.id).subscribe({
      next: (sessions) => (this.sessions = sessions),
      error: () => this.toastError('Could not load sessions'),
    });
  }

  revokeSession(session: any): void {
    this.adminService.revokeSession(this.sessionsUser.id, session.id).subscribe({
      next: () => {
        this.toastOk('Session revoked');
        this.openSessions(this.sessionsUser);
      },
      error: () => this.toastError('Could not revoke session'),
    });
  }

  // --- login activity --------------------------------------------------
  loadActivity(skip: number): void {
    this.loadingActivity = true;
    this.adminService.loginActivity(skip, this.activityRows).subscribe({
      next: (page) => {
        this.activity = page.records;
        this.activityTotal = page.total_records;
        this.loadingActivity = false;
      },
      error: () => {
        this.loadingActivity = false;
        this.toastError('Could not load login activity');
      },
    });
  }

  onActivityPage(event: any): void {
    this.loadActivity(event.first);
  }

  // --- report schedule -------------------------------------------------
  loadSchedule(): void {
    this.adminService
      .getReportSchedule()
      .subscribe((schedule) => (this.schedule = schedule));
  }

  saveSchedule(): void {
    if (!this.schedule) {
      return;
    }
    this.savingSchedule = true;
    const payload = {
      enabled: this.schedule.enabled,
      frequency: this.schedule.frequency,
      hour: this.schedule.hour,
      weekday: this.schedule.frequency === 'weekly' ? this.schedule.weekday : null,
      day_of_month:
        this.schedule.frequency === 'monthly' ? this.schedule.day_of_month : null,
    };
    this.adminService.updateReportSchedule(payload).subscribe({
      next: (schedule) => {
        this.schedule = schedule;
        this.savingSchedule = false;
        this.toastOk('Report schedule saved');
      },
      error: (error) => {
        this.savingSchedule = false;
        this.toastError(
          error?.error?.error?.message || 'Could not save the schedule'
        );
      },
    });
  }

  // --- audit trail ---------------------------------------------------
  loadAudit(page: number): void {
    this.loadingAudit = true;
    this.adminService.auditLog(Math.floor(page / this.auditRows) + 1, this.auditRows).subscribe({
      next: (res) => {
        this.audit = res.items;
        this.auditTotal = res.total;
        this.loadingAudit = false;
      },
      error: () => {
        this.loadingAudit = false;
        this.toastError('Could not load audit trail');
      },
    });
  }

  onAuditPage(event: any): void {
    this.loadAudit(event.first);
  }

  changeSummary(entry: any): string {
    if (!entry.changes) {
      return entry.action === 'create' ? 'created' : entry.action;
    }
    return Object.keys(entry.changes).join(', ');
  }

  // --- email / reset log ---------------------------------------------
  loadEmails(skip: number): void {
    this.loadingEmails = true;
    this.adminService.emailLog(skip, this.emailRows).subscribe({
      next: (res) => {
        this.emails = res.records;
        this.emailsTotal = res.total_records;
        this.loadingEmails = false;
      },
      error: () => {
        this.loadingEmails = false;
        this.toastError('Could not load email log');
      },
    });
  }

  onEmailPage(event: any): void {
    this.loadEmails(event.first);
  }

  // --- password actions ----------------------------------------------
  sendReset(user: any): void {
    this.adminService.sendReset(user.id).subscribe({
      next: () => this.toastOk(`Reset link sent to ${user.email}`),
      error: () => this.toastError('Could not send reset link'),
    });
  }

  openEditDialog(user: any): void {
    this.editUser = user;
    this.editEmail = user.email || '';
    this.editFullName = user.full_name || '';
    this.editDialogVisible = true;
  }

  saveEdit(): void {
    const email = this.editEmail.trim().toLowerCase();
    if (!email) {
      return;
    }
    this.savingEdit = true;
    this.adminService
      .updateUser(this.editUser.id, {
        email,
        full_name: this.editFullName.trim() || undefined,
      })
      .subscribe({
        next: (updated) => {
          this.editUser.email = updated.email;
          this.editUser.full_name = updated.full_name;
          this.savingEdit = false;
          this.editDialogVisible = false;
          this.toastOk('User updated');
        },
        error: (error) => {
          this.savingEdit = false;
          this.toastError(error?.error?.error?.message || 'Could not update user');
        },
      });
  }

  openPasswordDialog(user: any): void {
    this.passwordUser = user;
    this.newPassword = '';
    this.passwordDialogVisible = true;
  }

  setPassword(): void {
    if (!this.newPassword.trim()) {
      return;
    }
    this.savingPassword = true;
    this.adminService.setPassword(this.passwordUser.id, this.newPassword).subscribe({
      next: () => {
        this.savingPassword = false;
        this.passwordDialogVisible = false;
        this.toastOk(`Password set for ${this.passwordUser.email}`);
      },
      error: (error) => {
        this.savingPassword = false;
        this.toastError(error?.error?.error?.message || 'Could not set password');
      },
    });
  }

    private toastOk(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Admin', detail });
  }

  private toastError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Admin', detail });
  }
}
